import { useState } from "react";

import { IWard } from "../../../../@types/admin/AdminWard";
import { useWardDetails } from "../../../../hooks/queries/admin/wards";
import {
  ErrorMessage,
  FieldHint,
  LoadingOverlay,
  SectionCard,
  SectionHeader,
  SectionTitle,
  Tab,
  TabsContainer,
} from "../AdminPage.styles";
import { AuditLogPanel } from "../AuditLogPanel";
import { WardConfigurationForm } from "./WardConfigurationForm";
import { WardForm } from "./WardForm";

/** The audited table behind a ward's history (a ward IS a `cms_hospital`). */
const WARD_TABLE = "cms_hospital";
/** The audited table behind the ward's feature configuration. */
const WARD_CONFIG_TABLE = "WardConfigurations";

export type IWardDetailTab = "record" | "configuration";

const TABS: { id: IWardDetailTab; label: string }[] = [
  { id: "record", label: "Ward record" },
  { id: "configuration", label: "Configuration" },
];

export interface IWardDetailsPanelProps {
  wardId: number;
}

/** The ward-record audit trail, at the bottom of the Ward record tab. */
function WardRecordHistoryCard({ ward }: { ward: IWard }) {
  return (
    <SectionCard>
      <SectionHeader>
        <SectionTitle>History</SectionTitle>
      </SectionHeader>
      <AuditLogPanel
        tableName={WARD_TABLE}
        entityId={ward.id}
        title="Ward record changes"
      />
    </SectionCard>
  );
}

/** The configuration audit trail, at the bottom of the Configuration tab. */
function WardConfigHistoryCard({ ward }: { ward: IWard }) {
  return (
    <SectionCard>
      <SectionHeader>
        <SectionTitle>History</SectionTitle>
      </SectionHeader>
      {ward.wardConfigurationId ? (
        <AuditLogPanel
          tableName={WARD_CONFIG_TABLE}
          // Keyed on the CONFIGURATION row's id, not the ward's — same as
          // Retool's config history tab.
          entityId={ward.wardConfigurationId}
          title="Configuration changes"
        />
      ) : (
        <FieldHint>
          This ward has no configuration row yet, so there is no configuration
          history. One is created the first time its configuration is saved.
        </FieldHint>
      )}
    </SectionCard>
  );
}

/**
 * Ward record + feature configuration for one ward, each tab carrying its own
 * audit trail at the bottom — the portal port of Retool's `modalWard` edit
 * mode plus its `modalWardHistory` and config history tabs (EN-5151,
 * EN-5195).
 *
 * Tabs, not stacked cards, for the same reason as `HospitalDetailsPanel`: the
 * record form and the ~20-control configuration form bury each other on one
 * scroll.
 *
 * Shared so the Hospital Details wards tab can reuse it. **Embed it inline**,
 * never inside the global modal — the audit panel opens its own row
 * drill-down there, which is also why the ward edit form is a route rather
 * than the modal Retool used.
 */
export function WardDetailsPanel({ wardId }: IWardDetailsPanelProps) {
  const [tab, setTab] = useState<IWardDetailTab>("record");
  const { ward, isLoading, isError } = useWardDetails(wardId);

  if (isLoading) {
    return <LoadingOverlay>Loading ward...</LoadingOverlay>;
  }

  if (isError || !ward) {
    return (
      <ErrorMessage>
        Failed to load this ward. Try refreshing the page.
      </ErrorMessage>
    );
  }

  return (
    <>
      <TabsContainer>
        {TABS.map((entry) => (
          <Tab
            key={entry.id}
            type="button"
            active={tab === entry.id}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </Tab>
        ))}
      </TabsContainer>

      {tab === "record" && (
        <>
          <WardForm ward={ward} />
          <WardRecordHistoryCard ward={ward} />
        </>
      )}

      {tab === "configuration" && (
        <>
          <WardConfigurationForm ward={ward} />
          <WardConfigHistoryCard ward={ward} />
        </>
      )}
    </>
  );
}
